import React, {useLayoutEffect, useRef, useState} from "react";
import {TextInput, View, Pressable, Platform, ColorValue, StyleProp, ViewStyle} from "react-native";
import {defaultCountries, FlagEmoji, parseCountry, usePhoneInput} from 'react-international-phone';
import MainStyle from "../../MainStyle";
import {formatWithMask, useMaskedInputProps} from "react-native-mask-input";
import {outline_0} from "../constants";
import SelectModal from "../modals/SelectModal";
import Div from "../lib/Div";
import P from "../lib/P";
import {FormikErrors} from "formik";

const Countries = defaultCountries.map(el => el);

const us = Countries.splice(Countries.findIndex(el => {
  const [, iso2] = el;
  return iso2 === 'us';
}), 1)[0];

const ca = Countries.splice(Countries.findIndex(el => {
  const [, iso2] = el;
  return iso2 === 'ca';
}), 1)[0];

// Add the United States and Canada to the top.
Countries.unshift(us, ca);

type Props = {
  value: string, onChangeText: (val: string) => void, label?: string, w?: string | number, placeholder: string,
  onBlur?: (e: any) => void, error?: string | boolean | null | string[] | FormikErrors<any> | FormikErrors<any>[],
  mb?: number, withoutMask?: boolean, h?: number, bg?: ColorValue, fs?: number, rounded?: number
};

const PhoneInput = (props: Props) => {
  const {value, label, w, placeholder, error, mb, withoutMask, h, fs, bg, rounded} = props;
  const [country, setCountry] = useState(parseCountry(Countries[0]));
  const mask = country.format ? ['+', ...country.dialCode.split(''), ' ', ...country.format.split('').map(str => str === '.' ? /\d/ : str)] : ['+', ...country.dialCode.split(''), ' ', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
  const numberMask = country.format ? ['+', ...country.dialCode.split(''), ' ', ...country.format.replaceAll(' ', '').replaceAll('(', '').replaceAll(')', '').split('').map(str => str === '.' ? /\d/ : str)] : ['+', ...country.dialCode.split(''), ' ', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
  const phoneMaskedInputProps = useMaskedInputProps({
    value, onChangeText: props.onChangeText, mask: withoutMask ? numberMask : mask
  });
  const [visible, setVisible] = useState(false);
  const ref = useRef<any>();
  const CountryGuess = usePhoneInput({defaultCountry: 'us', value});
  const style: StyleProp<ViewStyle> = {height: h, backgroundColor: bg, borderRadius: rounded};

  const onChangeText = (text: string) => {
    const {unmasked: phone} = formatWithMask({text: text, mask});
    props.onChangeText(`+${country.dialCode}${phone}`);
  };

  const onFocus = () => {
    if (Platform.OS === 'web') {
      const {maxTouchPoints, platform} = window.navigator;

      if (maxTouchPoints && platform === 'Win32') {
        ref.current?.scrollIntoView({behavior: 'instant', block: 'center', inline: 'center'});
      }
    }
  };

  const onResume = (val?: string) => {
    setVisible(false);
    if (!val) return;

    const find = Countries.find(el => {
      const [, iso2] = el;
      return iso2 === val;
    });

    if (find) {
      setCountry(parseCountry(find));
      props.onChangeText('');
    }
  };

  useLayoutEffect(() => {
    if (value) {
      const findCountry = Countries.find(el => {
        const [, iso2] = el;
        return iso2 === CountryGuess.country;
      });

      if (findCountry) setCountry(parseCountry(findCountry));
    }
  }, []);

  return (
    <Div w={w} mb={mb}>
      {label && <P ta="center" fs={14} fw="500" tt="uppercase" mb={1} opacity={0.5}>{label}</P>}

      <View style={[...styles.inputWrapper, error ? MainStyle.b_c_warn : {}, style]} ref={ref}>
        <Pressable style={styles.icon} onPress={() => setVisible(true)}>
          <FlagEmoji iso2={country.iso2} style={{width: 33, height: 33}}/>
        </Pressable>

        <TextInput
          style={[...styles.input, {fontSize: fs}]} keyboardType="phone-pad" onBlur={props.onBlur}
          value={phoneMaskedInputProps.value} onChangeText={onChangeText}
          placeholder={`+${country.dialCode} ${placeholder}`} onFocus={onFocus}
        />
      </View>

      <SelectModal visible={visible} onResume={onResume} value={country.iso2} countries={Countries}/>
    </Div>
  );
};

const styles = {
  inputWrapper: [
    MainStyle.row, MainStyle.h_66, MainStyle.b_r_200, MainStyle.b_w_1, MainStyle.b_c_settings_border,
    MainStyle.bg_loading_login, MainStyle.relative, MainStyle.a_i_c
  ],
  icon: [
    MainStyle.m_l_32, MainStyle.w_24, MainStyle.h_24, MainStyle.b_r_20, MainStyle.o_hidden, MainStyle.a_i_c,
    MainStyle.j_c_c
  ],
  input: [
    MainStyle.m_l_8, MainStyle.c_white, MainStyle.Barlow500, MainStyle.f_s_24, outline_0,
    MainStyle.flex1
  ]
};

export default React.memo(PhoneInput);
